<template>
  <div class="fixed inset-0 z-50 overflow-y-auto wizard-modal seooptimization-wizard-modal">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity wizard-backdrop"></div>

      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full wizard-dialog">
        <div class="bg-white px-6 pt-5 pb-4 wizard-body">
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
            <div>
              <h3 class="text-lg font-medium text-gray-900">SEO Optimization</h3>
              <p class="text-sm text-gray-500 mt-1">Step {{ currentStep }} of {{ totalSteps }}: {{ stepTitles[currentStep - 1] }}</p>
            </div>
            <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="mb-6">
            <div class="flex items-center">
              <div
                v-for="step in totalSteps"
                :key="step"
                class="flex items-center"
                :class="{ 'flex-1': step < totalSteps }"
              >
                <div
                  class="flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium"
                  :class="step <= currentStep
                    ? 'bg-violet-600 border-violet-600 text-white'
                    : 'border-gray-300 text-gray-500'"
                >
                  {{ step }}
                </div>
                <div
                  v-if="step < totalSteps"
                  class="flex-1 h-0.5 mx-4"
                  :class="step < currentStep ? 'bg-violet-600' : 'bg-gray-300'"
                ></div>
              </div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="space-y-6">
            <!-- Step 1: SEO Goals -->
            <div v-if="currentStep === 1">
              <h4 class="text-lg font-medium text-gray-900 mb-4">What are your SEO goals?</h4>
              <div class="space-y-3">
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.goals.increaseTraffic"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Increase organic traffic</div>
                    <div class="text-xs text-gray-500">Improve search engine rankings and visibility</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.goals.improveRankings"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Improve keyword rankings</div>
                    <div class="text-xs text-gray-500">Target specific keywords for better positions</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.goals.technicalSEO"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Fix technical SEO issues</div>
                    <div class="text-xs text-gray-500">Optimize page speed, mobile-friendliness, crawlability</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.goals.localSEO"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Improve local SEO</div>
                    <div class="text-xs text-gray-500">Target local customers and location-based searches</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.goals.contentOptimization"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Optimize content for SEO</div>
                    <div class="text-xs text-gray-500">Improve meta tags, headings, and content structure</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Step 2: Keywords & Competition -->
            <div v-if="currentStep === 2">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Keywords & Competition</h4>
              <div class="space-y-4">
                <div>
                  <label for="targetKeywords" class="block text-sm font-medium text-gray-700 mb-1">
                    Target Keywords
                  </label>
                  <textarea
                    id="targetKeywords"
                    v-model="seoData.targetKeywords"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="web design, responsive websites, SEO optimization..."
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Enter keywords separated by commas</p>
                </div>
                <div>
                  <label for="competitors" class="block text-sm font-medium text-gray-700 mb-1">
                    Competitor Websites (optional)
                  </label>
                  <textarea
                    id="competitors"
                    v-model="seoData.competitors"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="competitor1.com, competitor2.com..."
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Analyze competitor strategies for insights</p>
                </div>
                <div>
                  <label for="targetAudience" class="block text-sm font-medium text-gray-700 mb-1">
                    Target Audience
                  </label>
                  <input
                    id="targetAudience"
                    v-model="seoData.targetAudience"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Small business owners, developers, designers..."
                  />
                </div>
                <div>
                  <label for="location" class="block text-sm font-medium text-gray-700 mb-1">
                    Target Location (optional)
                  </label>
                  <input
                    id="location"
                    v-model="seoData.location"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="New York, London, Global..."
                  />
                  <p class="text-xs text-gray-500 mt-1">For local SEO targeting</p>
                </div>
              </div>
            </div>

            <!-- Step 3: Focus Areas -->
            <div v-if="currentStep === 3">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Which areas should we focus on?</h4>
              <div class="space-y-3">
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.metaTags"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Meta titles and descriptions</div>
                    <div class="text-xs text-gray-500">Optimize page titles and meta descriptions for better CTR</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.headingStructure"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Heading structure (H1, H2, H3)</div>
                    <div class="text-xs text-gray-500">Improve content hierarchy and keyword targeting</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.imageOptimization"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Image optimization</div>
                    <div class="text-xs text-gray-500">Add alt text, optimize file sizes, and improve loading speed</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.internalLinking"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Internal linking</div>
                    <div class="text-xs text-gray-500">Create better navigation and distribute page authority</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.pageSpeed"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Page speed optimization</div>
                    <div class="text-xs text-gray-500">Improve loading times and Core Web Vitals</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.mobileOptimization"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Mobile optimization</div>
                    <div class="text-xs text-gray-500">Ensure mobile-friendly design and functionality</div>
                  </div>
                </label>
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="seoData.focusAreas.schemaMarkup"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div>
                    <div class="text-sm font-medium text-gray-900">Schema markup</div>
                    <div class="text-xs text-gray-500">Add structured data for better search results</div>
                  </div>
                </label>
              </div>
            </div>

            <!-- Step 4: AEO & GEO Guidance -->
            <div v-if="currentStep === 4">
              <h4 class="text-lg font-medium text-gray-900 mb-2">AEO & GEO Guidance</h4>
              <p class="text-sm text-gray-500 mb-4">Optionally provide guidance for Answer Engine Optimization and Generative Engine Optimization. These fields are entirely optional.</p>

              <div class="space-y-5">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <label for="aeoGuidance" class="block text-sm font-medium text-gray-700">
                      AEO Guidance
                    </label>
                    <span class="text-xs text-gray-400">(optional)</span>
                  </div>
                  <p class="text-xs text-gray-500 mb-2">Answer Engine Optimization helps your content appear as direct answers in AI assistants (e.g. ChatGPT, Perplexity) and featured snippets.</p>
                  <textarea
                    id="aeoGuidance"
                    v-model="seoData.aeoGuidance"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. Structure FAQ sections with concise answers, add how-to steps, use question-based headings..."
                  ></textarea>
                </div>

                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <label for="geoGuidance" class="block text-sm font-medium text-gray-700">
                      GEO Guidance
                    </label>
                    <span class="text-xs text-gray-400">(optional)</span>
                  </div>
                  <p class="text-xs text-gray-500 mb-2">Generative Engine Optimization helps your content be cited and referenced by generative AI search results (e.g. Google AI Overviews, Bing Copilot).</p>
                  <textarea
                    id="geoGuidance"
                    v-model="seoData.geoGuidance"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. Add authoritative citations, include statistical claims with sources, use clear definitions..."
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Step 5: Priority Pages & Review -->
            <div v-if="currentStep === 5">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Priority Pages & Review</h4>
              <div class="space-y-4">
                <div>
                  <label for="priorityPages" class="block text-sm font-medium text-gray-700 mb-1">
                    Which pages should we prioritize?
                  </label>
                  <textarea
                    id="priorityPages"
                    v-model="seoData.priorityPages"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Homepage, About page, Services page, Blog posts..."
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">List the most important pages to optimize first</p>
                </div>

                <div>
                  <label for="additionalNotes" class="block text-sm font-medium text-gray-700 mb-1">
                    Additional Requirements (optional)
                  </label>
                  <textarea
                    id="additionalNotes"
                    v-model="seoData.additionalNotes"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Any specific SEO requirements or current issues you're experiencing..."
                  ></textarea>
                </div>

                <!-- Summary -->
                <div class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-900 mb-2">SEO Optimization Summary</h5>
                  <div class="text-xs text-gray-600 space-y-1">
                    <div><strong>Goals:</strong> {{ selectedGoalsText }}</div>
                    <div v-if="seoData.targetKeywords"><strong>Keywords:</strong> {{ seoData.targetKeywords }}</div>
                    <div><strong>Focus Areas:</strong> {{ selectedFocusAreasText }}</div>
                    <div v-if="seoData.aeoGuidance"><strong>AEO Guidance:</strong> {{ seoData.aeoGuidance }}</div>
                    <div v-if="seoData.geoGuidance"><strong>GEO Guidance:</strong> {{ seoData.geoGuidance }}</div>
                    <div v-if="seoData.priorityPages"><strong>Priority Pages:</strong> {{ seoData.priorityPages }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-6 py-3 flex items-center justify-between">
          <button
            v-if="currentStep > 1"
            @click="previousStep"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div v-else></div>

          <div class="space-x-3">
            <button
              @click="$emit('close')"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              v-if="currentStep < totalSteps"
              @click="nextStep"
              :disabled="!canProceed"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              v-else
              @click="createSEOOptimization"
              :disabled="!canProceed"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              {{ isSubmitting ? 'Optimizing...' : 'Start SEO Optimization' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive } from 'vue'

export default {
  name: 'SeoOptimizationModal',
  emits: ['close', 'create'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const totalSteps = 5
    const isSubmitting = ref(false)

    const stepTitles = [
      'SEO Goals',
      'Keywords & Competition',
      'Focus Areas',
      'AEO & GEO Guidance',
      'Priority Pages & Review'
    ]

    const seoData = reactive({
      goals: {
        increaseTraffic: false,
        improveRankings: false,
        technicalSEO: false,
        localSEO: false,
        contentOptimization: false
      },
      targetKeywords: '',
      competitors: '',
      targetAudience: '',
      location: '',
      focusAreas: {
        metaTags: false,
        headingStructure: false,
        imageOptimization: false,
        internalLinking: false,
        pageSpeed: false,
        mobileOptimization: false,
        schemaMarkup: false
      },
      aeoGuidance: '',
      geoGuidance: '',
      priorityPages: '',
      additionalNotes: ''
    })

    const selectedGoalsText = computed(() => {
      const goals = []
      if (seoData.goals.increaseTraffic) goals.push('Increase traffic')
      if (seoData.goals.improveRankings) goals.push('Improve rankings')
      if (seoData.goals.technicalSEO) goals.push('Technical SEO')
      if (seoData.goals.localSEO) goals.push('Local SEO')
      if (seoData.goals.contentOptimization) goals.push('Content optimization')
      return goals.length > 0 ? goals.join(', ') : 'None selected'
    })

    const selectedFocusAreasText = computed(() => {
      const areas = []
      if (seoData.focusAreas.metaTags) areas.push('Meta tags')
      if (seoData.focusAreas.headingStructure) areas.push('Headings')
      if (seoData.focusAreas.imageOptimization) areas.push('Images')
      if (seoData.focusAreas.internalLinking) areas.push('Internal links')
      if (seoData.focusAreas.pageSpeed) areas.push('Page speed')
      if (seoData.focusAreas.mobileOptimization) areas.push('Mobile')
      if (seoData.focusAreas.schemaMarkup) areas.push('Schema markup')
      return areas.length > 0 ? areas.join(', ') : 'None selected'
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1:
          return Object.values(seoData.goals).some(goal => goal)
        case 2:
          return seoData.targetKeywords.trim() !== ''
        case 3:
          return Object.values(seoData.focusAreas).some(area => area)
        case 4:
          return true
        case 5:
          return true
        default:
          return false
      }
    })

    const nextStep = () => {
      if (currentStep.value < totalSteps && canProceed.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const createSEOOptimization = () => {
      if (!canProceed.value) return

      isSubmitting.value = true

      let prompt = `Optimize this website for SEO with the following specifications:\n\n`

      // Goals
      prompt += `**SEO Goals:**\n`
      const selectedGoals = []
      if (seoData.goals.increaseTraffic) selectedGoals.push('- Increase organic traffic and search visibility')
      if (seoData.goals.improveRankings) selectedGoals.push('- Improve keyword rankings')
      if (seoData.goals.technicalSEO) selectedGoals.push('- Fix technical SEO issues')
      if (seoData.goals.localSEO) selectedGoals.push('- Improve local SEO')
      if (seoData.goals.contentOptimization) selectedGoals.push('- Optimize content for search engines')
      prompt += selectedGoals.join('\n') + '\n\n'

      // Keywords and targeting
      if (seoData.targetKeywords) {
        prompt += `**Target Keywords:** ${seoData.targetKeywords}\n\n`
      }
      if (seoData.targetAudience) {
        prompt += `**Target Audience:** ${seoData.targetAudience}\n\n`
      }
      if (seoData.location) {
        prompt += `**Target Location:** ${seoData.location}\n\n`
      }
      if (seoData.competitors) {
        prompt += `**Competitor Analysis:** Analyze and learn from: ${seoData.competitors}\n\n`
      }

      // Focus areas
      prompt += `**Focus Areas:**\n`
      const selectedAreas = []
      if (seoData.focusAreas.metaTags) selectedAreas.push('- Optimize meta titles and descriptions for better CTR')
      if (seoData.focusAreas.headingStructure) selectedAreas.push('- Improve heading structure (H1, H2, H3) with target keywords')
      if (seoData.focusAreas.imageOptimization) selectedAreas.push('- Add alt text and optimize images for faster loading')
      if (seoData.focusAreas.internalLinking) selectedAreas.push('- Improve internal linking structure')
      if (seoData.focusAreas.pageSpeed) selectedAreas.push('- Optimize page speed and Core Web Vitals')
      if (seoData.focusAreas.mobileOptimization) selectedAreas.push('- Ensure mobile-friendly design and functionality')
      if (seoData.focusAreas.schemaMarkup) selectedAreas.push('- Add structured data markup for better search results')
      prompt += selectedAreas.join('\n') + '\n\n'

      // AEO Guidance
      prompt += `**AEO (Answer Engine Optimization) Guidance:**\n`
      if (seoData.aeoGuidance.trim()) {
        prompt += seoData.aeoGuidance.trim() + '\n\n'
      } else {
        prompt += '- Structure content with clear question-and-answer patterns where appropriate\n'
        prompt += '- Use concise, direct answers near the top of sections for featured-snippet eligibility\n\n'
      }

      // GEO Guidance
      prompt += `**GEO (Generative Engine Optimization) Guidance:**\n`
      if (seoData.geoGuidance.trim()) {
        prompt += seoData.geoGuidance.trim() + '\n\n'
      } else {
        prompt += '- Include authoritative, citable statements that generative AI models can reference\n'
        prompt += '- Use clear definitions and structured data to improve citation likelihood\n\n'
      }

      // Priority pages
      if (seoData.priorityPages) {
        prompt += `**Priority Pages:** Focus on optimizing these pages first: ${seoData.priorityPages}\n\n`
      }

      // Additional requirements
      if (seoData.additionalNotes) {
        prompt += `**Additional Requirements:** ${seoData.additionalNotes}\n\n`
      }

      // Placeholder AEO/GEO meta tags
      prompt += `**AEO/GEO Meta Tags:**\n`
      prompt += `Add the following placeholder meta tags to every page's <head> (inside useHead or nuxt.config). These are placeholders for future AEO/GEO integration:\n`
      prompt += `- <meta name="aeo-optimized" content="PLACEHOLDER — replace with AEO optimization status once validated">\n`
      prompt += `- <meta name="aeo-answer-ready" content="PLACEHOLDER — replace with primary answer snippet for this page">\n`
      prompt += `- <meta name="geo-optimized" content="PLACEHOLDER — replace with GEO optimization status once validated">\n`
      prompt += `- <meta name="geo-citation-ready" content="PLACEHOLDER — replace with primary citable statement for this page">\n\n`

      prompt += `Please implement these SEO improvements systematically, ensuring all changes follow current SEO best practices and maintain the website's design integrity.`

      emit('create', {
        prompt,
        seoData: { ...seoData },
        aeoGuidance: seoData.aeoGuidance,
        geoGuidance: seoData.geoGuidance
      })
    }

    return {
      currentStep,
      totalSteps,
      stepTitles,
      seoData,
      selectedGoalsText,
      selectedFocusAreasText,
      canProceed,
      isSubmitting,
      nextStep,
      previousStep,
      createSEOOptimization
    }
  }
}
</script>

<style src="./wizard-glass-modal.css"></style>

